import { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Pencil, Trash2, Star, Package, MapPin, MessageSquare, Plus, Search, ArrowRight, HandCoins, ShoppingBag, ArrowLeftRight, Clock, ChevronRight, Bell, DollarSign, TrendingUp, XCircle, Award } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { getImageUrl } from "@/lib/imageUtils";
import { ALL_LOCATIONS, CARIBBEAN_LOCATIONS, locationToSlug } from "@/lib/constants";

const STATUS_COLORS = {
  pending: "bg-amber-50 text-amber-700 border-amber-200",
  confirmed: "bg-blue-50 text-blue-700 border-blue-200",
  active: "bg-emerald-50 text-emerald-700 border-emerald-200",
  returned: "bg-stone-100 text-stone-600 border-stone-200",
  cancelled: "bg-red-50 text-red-600 border-red-200",
};

function RoleSelector({ onSelect }) {
  return (
    <div className="flex items-center justify-center px-4 py-12" data-testid="role-selector">
      <div className="max-w-2xl w-full">
        <div className="text-center mb-10">
          <h1 className="font-heading font-bold text-3xl sm:text-4xl text-stone-900">What brings you here today?</h1>
          <p className="text-stone-500 mt-2 text-base">You can always switch between lending and borrowing</p>
        </div>
        <div className="grid sm:grid-cols-2 gap-6">
          <button
            onClick={() => onSelect("lender")}
            className="group p-8 rounded-2xl border-2 border-stone-200 bg-white hover:border-coral hover:shadow-xl hover:shadow-orange-100/50 transition-all duration-300 text-left cursor-pointer"
            data-testid="role-lender-btn"
          >
            <div className="w-14 h-14 rounded-2xl bg-coral-light flex items-center justify-center mb-5 group-hover:bg-coral-light transition-colors">
              <HandCoins className="w-7 h-7 text-coral" />
            </div>
            <h2 className="font-heading font-bold text-xl text-stone-900 mb-2">I'm Lending</h2>
            <p className="text-stone-500 text-sm leading-relaxed">
              Manage your item listings, review incoming booking requests, and track active bookings.
            </p>
            <div className="mt-5 flex items-center gap-1.5 text-coral text-sm font-medium group-hover:gap-2.5 transition-all">
              Go to Lender Dashboard <ArrowRight className="w-4 h-4" />
            </div>
          </button>
          <button
            onClick={() => onSelect("borrower")}
            className="group p-8 rounded-2xl border-2 border-stone-200 bg-white hover:border-emerald-500 hover:shadow-xl hover:shadow-emerald-100/50 transition-all duration-300 text-left cursor-pointer"
            data-testid="role-borrower-btn"
          >
            <div className="w-14 h-14 rounded-2xl bg-emerald-50 flex items-center justify-center mb-5 group-hover:bg-emerald-100 transition-colors">
              <ShoppingBag className="w-7 h-7 text-emerald-600" />
            </div>
            <h2 className="font-heading font-bold text-xl text-stone-900 mb-2">I'm Borrowing</h2>
            <p className="text-stone-500 text-sm leading-relaxed">
              View your bookings, find items to borrow, message lenders, and leave reviews.
            </p>
            <div className="mt-5 flex items-center gap-1.5 text-emerald-600 text-sm font-medium group-hover:gap-2.5 transition-all">
              Go to Borrower Dashboard <ArrowRight className="w-4 h-4" />
            </div>
          </button>
        </div>
      </div>
    </div>
  );
}

function LenderDashboard({ user, myItems, bookings, deleteItem, updateStatus }) {
  const lenderBookings = bookings.filter((b) => b.lender_id === user.id);
  const completedBookings = lenderBookings.filter((b) => b.status === "returned");
  const totalEarnings = completedBookings.reduce((sum, b) => sum + (b.lender_payout || b.total_price || 0), 0);
  const pendingEarnings = lenderBookings.filter((b) => b.status === "active" || b.status === "confirmed").reduce((sum, b) => sum + (b.lender_payout || b.total_price || 0), 0);

  const isEndDate = (endDateStr) => {
    if (!endDateStr) return false;
    const today = new Date().toISOString().split("T")[0];
    return endDateStr <= today;
  };

  const promoEligible = user.promo_eligible === true;
  const listingCount = user.listing_count || 0;
  const freeListingsUsed = Math.min(listingCount, 10);
  const freeListingsRemaining = Math.max(0, 10 - listingCount);
  
  return (
    <div data-testid="lender-dashboard">
      {/* Promo Banner */}
      {promoEligible && (
        <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-2xl p-5 mb-6" data-testid="promo-banner">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center shrink-0">
              <Award className="w-6 h-6 text-amber-600" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <h3 className="font-heading font-bold text-stone-900">Promotional Member</h3>
                <Badge className="bg-amber-100 text-amber-700 border-amber-300 text-xs rounded-full">First 10 in {user.promo_location || user.location}</Badge>
              </div>
              <p className="text-sm text-stone-600 mb-3">
                Your first 10 listings have a <span className="font-semibold text-amber-700">0% platform fee</span>. Standard 7% applies starting from listing #11.
              </p>
              <div className="flex items-center gap-4">
                <div className="bg-white rounded-lg px-3 py-1.5 border border-amber-200">
                  <span className="text-xs text-stone-500">Fee-free listings used</span>
                  <p className="font-heading font-bold text-amber-700">{freeListingsUsed}/10</p>
                </div>
                {freeListingsRemaining > 0 && (
                  <div className="bg-white rounded-lg px-3 py-1.5 border border-amber-200">
                    <span className="text-xs text-stone-500">Remaining</span>
                    <p className="font-heading font-bold text-emerald-600">{freeListingsRemaining}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
        {[
          { label: "Total Earnings", value: `$${totalEarnings.toFixed(0)}`, color: "bg-emerald-50 text-emerald-700", icon: DollarSign },
          { label: "Pending Payout", value: `$${pendingEarnings.toFixed(0)}`, color: "bg-amber-50 text-amber-700", icon: Clock },
          { label: "Active Bookings", value: lenderBookings.filter((b) => b.status === "active").length, color: "bg-blue-50 text-blue-700", icon: Package },
          { label: "Completed", value: completedBookings.length, color: "bg-stone-100 text-stone-600", icon: TrendingUp },
        ].map((s) => (
          <div key={s.label} className="bg-white rounded-2xl border border-stone-200 p-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs text-stone-500">{s.label}</p>
              <s.icon className={`w-4 h-4 ${s.color.split(" ")[1]}`} />
            </div>
            <p className={`font-heading font-bold text-2xl ${s.color.split(" ")[1]}`}>{s.value}</p>
          </div>
        ))}
      </div>

      <Tabs defaultValue="listings">
        <TabsList className="mb-6">
          <TabsTrigger value="listings" data-testid="lender-tab-listings">My Listings ({myItems.length})</TabsTrigger>
          <TabsTrigger value="requests" data-testid="lender-tab-requests">Bookings ({lenderBookings.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="listings">
          {myItems.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-2xl border border-stone-200">
              <Package className="w-12 h-12 mx-auto text-stone-300 mb-3" />
              <p className="text-stone-500 mb-4">You haven't listed any items yet</p>
              <Link to="/create-listing">
                <Button className="rounded-full bg-coral hover:bg-coral text-white">Create Your First Listing</Button>
              </Link>
            </div>
          ) : (
            <div className="grid gap-4">
              {myItems.map((item) => (
                <div key={item.id} className="bg-white rounded-2xl border border-stone-200 p-4 flex gap-4 items-center hover:shadow-md transition-shadow" data-testid={`my-item-${item.id}`}>
                  <img src={getImageUrl(item.images?.[0]) || "https://via.placeholder.com/100"} alt={item.title} className="w-20 h-20 rounded-xl object-cover shrink-0" />
                  <div className="flex-1 min-w-0">
                    <Link to={`/items/${item.id}`} className="font-heading font-semibold text-stone-900 hover:text-coral text-sm">{item.title}</Link>
                    <div className="flex items-center gap-2 mt-1 text-xs text-stone-500">
                      <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{item.location}</span>
                      <span>${item.price_per_day}/day</span>
                      <Badge variant="outline" className="text-xs rounded-full">{item.category}</Badge>
                      {item.platform_fee_waived && (
                        <Badge className="bg-amber-50 text-amber-700 border-amber-200 text-xs rounded-full" data-testid={`fee-free-badge-${item.id}`}>Fee-free</Badge>
                      )}
                      {!item.platform_fee_waived && item.listing_sequence_number && (
                        <span className="text-stone-400 text-xs">7% fee</span>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <Link to={`/edit-listing/${item.id}`}>
                      <Button variant="outline" size="icon" className="rounded-xl w-9 h-9" data-testid={`edit-item-${item.id}`}>
                        <Pencil className="w-4 h-4 text-stone-600" />
                      </Button>
                    </Link>
                    <Button variant="outline" size="icon" className="rounded-xl w-9 h-9" onClick={() => deleteItem(item.id)} data-testid={`delete-item-${item.id}`}>
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="requests">
          {lenderBookings.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-2xl border border-stone-200">
              <Package className="w-12 h-12 mx-auto text-stone-300 mb-3" />
              <p className="text-stone-500">No booking requests yet</p>
              <p className="text-stone-400 text-sm mt-1">When someone books one of your items, it will appear here</p>
            </div>
          ) : (
            <div className="grid gap-4">
              {lenderBookings.map((b) => (
                <div key={b.id} className="bg-white rounded-2xl border border-stone-200 p-4" data-testid={`lender-booking-${b.id}`}>
                  <div className="flex gap-4 items-start">
                    <img src={getImageUrl(b.item?.images?.[0]) || "https://via.placeholder.com/80"} alt={b.item?.title} className="w-16 h-16 rounded-xl object-cover shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <Link to={`/items/${b.item_id}`} className="font-heading font-semibold text-sm text-stone-900 hover:text-coral">{b.item?.title}</Link>
                        <Badge variant="outline" className={`text-xs rounded-full ${STATUS_COLORS[b.status]}`}>{b.status}</Badge>
                      </div>
                      <p className="text-xs text-stone-500">{b.start_date} to {b.end_date} &middot; ${b.total_price} &middot; {b.delivery_method}</p>
                      <p className="text-xs text-stone-600 mt-1 font-medium">Borrower: {b.borrower?.name}</p>
                    </div>
                    <div className="flex gap-2 shrink-0 flex-wrap">
                      {b.status === "pending" && (
                        <>
                          <Button size="sm" className="rounded-full bg-coral hover:bg-coral text-white text-xs btn-press" onClick={() => updateStatus(b.id, "confirmed")} data-testid={`confirm-booking-${b.id}`}>Confirm</Button>
                          <Button size="sm" variant="outline" className="rounded-full text-xs" onClick={() => updateStatus(b.id, "cancelled")} data-testid={`decline-booking-${b.id}`}>Decline</Button>
                        </>
                      )}
                      {(b.status === "confirmed" || b.status === "active") && isEndDate(b.end_date) && (
                        <Button size="sm" className="rounded-full text-xs bg-stone-800 hover:bg-stone-900 text-white btn-press" onClick={() => updateStatus(b.id, "returned")} data-testid={`return-booking-${b.id}`}>Mark as Returned</Button>
                      )}
                      <Link to={`/messages?to=${b.borrower_id}&item=${b.item_id}`}>
                        <Button size="sm" variant="ghost" className="rounded-full text-xs gap-1"><MessageSquare className="w-3 h-3" /></Button>
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

function BorrowerDashboard({ user, bookings, cancelBooking, reviewModal, setReviewModal, reviewData, setReviewData, submitReview }) {
  const borrowerBookings = bookings.filter((b) => b.borrower_id === user.id);
  const navigate = useNavigate();
  const [locationQuery, setLocationQuery] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [filteredLocations, setFilteredLocations] = useState([]);
  const searchRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleLocationChange = (e) => {
    const value = e.target.value;
    setLocationQuery(value);
    if (value.trim().length > 0) {
      const filtered = ALL_LOCATIONS.filter((loc) =>
        loc.name.toLowerCase().includes(value.toLowerCase())
      ).slice(0, 6);
      setFilteredLocations(filtered);
      setShowSuggestions(true);
    } else {
      setShowSuggestions(false);
    }
  };

  const handleLocationSelect = (location) => {
    setLocationQuery("");
    setShowSuggestions(false);
    navigate(`/${locationToSlug(location.name)}`);
  };

  return (
    <div data-testid="borrower-dashboard">
      {/* Where do you want to borrow? */}
      <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-3xl p-8 sm:p-12 mb-8 border border-emerald-100">
        <div className="max-w-xl">
          <h2 className="font-heading font-bold text-2xl sm:text-3xl text-stone-900 mb-2">
            Where do you want to borrow?
          </h2>
          <p className="text-stone-600 mb-6">
            Search for your destination to find items available for borrowing
          </p>
          <div className="relative" ref={searchRef}>
            <div className="flex items-center bg-white rounded-full shadow-lg border border-stone-200 p-1.5 hover:shadow-xl transition-shadow">
              <div className="relative flex-1">
                <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-emerald-500" />
                <input
                  data-testid="borrower-location-search"
                  type="text"
                  placeholder="Enter a destination..."
                  value={locationQuery}
                  onChange={handleLocationChange}
                  onFocus={() => locationQuery.trim() && setShowSuggestions(true)}
                  className="w-full pl-12 pr-4 py-3 rounded-full text-sm bg-transparent focus:outline-none placeholder:text-stone-400"
                />
              </div>
              <Button 
                type="button"
                onClick={() => filteredLocations.length > 0 && handleLocationSelect(filteredLocations[0])}
                className="rounded-full px-6 h-10 bg-emerald-600 hover:bg-emerald-700 text-white btn-press"
                data-testid="borrower-search-btn"
              >
                Search
              </Button>
            </div>
            {showSuggestions && filteredLocations.length > 0 && (
              <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-lg border border-stone-200 overflow-hidden z-50">
                {filteredLocations.map((loc) => (
                  <button
                    key={loc.name}
                    type="button"
                    onClick={() => handleLocationSelect(loc)}
                    className="w-full px-4 py-3 text-left hover:bg-emerald-50 flex items-center gap-3 transition-colors"
                  >
                    <MapPin className="w-4 h-4 text-emerald-500" />
                    <span className="text-sm text-stone-700">{loc.name}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          {/* Quick destinations */}
          <div className="mt-4 flex flex-wrap gap-2">
            <span className="text-xs text-stone-500 mr-1">Popular:</span>
            {CARIBBEAN_LOCATIONS.slice(0, 4).map((loc) => (
              <button
                key={loc.name}
                onClick={() => handleLocationSelect(loc)}
                className="text-xs px-3 py-1.5 rounded-full bg-white border border-emerald-200 text-emerald-700 hover:bg-emerald-50 hover:border-emerald-300 transition-all cursor-pointer"
                data-testid={`quick-dest-${loc.name.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {loc.name}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* My Bookings Section */}
      {borrowerBookings.length > 0 && (() => {
        const paidBookings = borrowerBookings.filter((b) => ['confirmed', 'active', 'returned', 'completed'].includes(b.status));
        const unpaidBookings = borrowerBookings.filter((b) => b.status === 'pending');
        return (
          <>
            {paidBookings.length > 0 && (
              <>
                <h3 className="font-heading font-semibold text-lg text-stone-900 mb-4">My Bookings</h3>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
                  {[
                    { label: "Active Borrows", value: paidBookings.filter((b) => b.status === "active").length, color: "bg-emerald-50 text-emerald-700" },
                    { label: "Confirmed", value: paidBookings.filter((b) => b.status === "confirmed").length, color: "bg-blue-50 text-blue-700" },
                    { label: "Completed", value: paidBookings.filter((b) => b.status === "returned" || b.status === "completed").length, color: "bg-stone-100 text-stone-600" },
                    { label: "Total Paid", value: paidBookings.length, color: "bg-blue-50 text-blue-700" },
                  ].map((s) => (
                    <div key={s.label} className="bg-white rounded-2xl border border-stone-200 p-4">
                      <p className="text-xs text-stone-500 mb-1">{s.label}</p>
                      <p className={`font-heading font-bold text-2xl ${s.color.split(" ")[1]}`}>{s.value}</p>
                    </div>
                  ))}
                </div>
                <div className="grid gap-4 mb-8">
                  {paidBookings.map((b) => (
                    <div key={b.id} className="bg-white rounded-2xl border border-stone-200 p-4" data-testid={`borrower-booking-${b.id}`}>
                      <div className="flex gap-4 items-start">
                        <img src={getImageUrl(b.item?.images?.[0]) || "https://via.placeholder.com/80"} alt={b.item?.title} className="w-16 h-16 rounded-xl object-cover shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <Link to={`/items/${b.item_id}`} className="font-heading font-semibold text-sm text-stone-900 hover:text-coral">{b.item?.title}</Link>
                            <Badge variant="outline" className={`text-xs rounded-full ${STATUS_COLORS[b.status]}`}>{b.status}</Badge>
                          </div>
                          <p className="text-xs text-stone-500">{b.start_date} to {b.end_date} &middot; ${b.total_price} &middot; {b.delivery_method}</p>
                          <p className="text-xs text-stone-600 mt-1 font-medium">Lender: {b.lender?.name}</p>
                        </div>
                        <div className="flex gap-2 shrink-0 flex-wrap">
                          {(b.status === "confirmed" || b.status === "pending") && (
                            <Button size="sm" variant="outline" className="rounded-full text-xs gap-1 text-red-600 border-red-200 hover:bg-red-50" onClick={() => cancelBooking(b.id)} data-testid={`cancel-booking-${b.id}`}>
                              <XCircle className="w-3 h-3" /> Cancel
                            </Button>
                          )}
                          {b.status === "returned" && !b.has_review && (
                            <Button size="sm" variant="outline" className="rounded-full text-xs gap-1" onClick={() => setReviewModal(b)} data-testid={`review-booking-${b.id}`}>
                              <Star className="w-3 h-3" /> Leave Review
                            </Button>
                          )}
                          <Link to={`/messages?to=${b.lender_id}&item=${b.item_id}`}>
                            <Button size="sm" variant="ghost" className="rounded-full text-xs gap-1" data-testid={`message-lender-${b.id}`}>
                              <MessageSquare className="w-3 h-3" /> Message
                            </Button>
                          </Link>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}

            {unpaidBookings.length > 0 && (
              <>
                <h3 className="font-heading font-semibold text-lg text-stone-900 mb-2">Unsuccessful Bookings</h3>
                <p className="text-sm text-stone-500 mb-4 font-ui">These bookings were not completed. You can try again or they will be automatically removed.</p>
                <div className="grid gap-4 mb-8" data-testid="unsuccessful-bookings">
                  {unpaidBookings.map((b) => (
                    <div key={b.id} className="bg-stone-50 rounded-2xl border border-stone-200 p-4 opacity-75" data-testid={`unpaid-booking-${b.id}`}>
                      <div className="flex gap-4 items-start">
                        <img src={getImageUrl(b.item?.images?.[0]) || "https://via.placeholder.com/80"} alt={b.item?.title} className="w-16 h-16 rounded-xl object-cover shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <Link to={`/items/${b.item_id}`} className="font-heading font-semibold text-sm text-stone-900 hover:text-coral">{b.item?.title}</Link>
                            <Badge variant="outline" className="text-xs rounded-full bg-amber-50 text-amber-700 border-amber-200">Unpaid</Badge>
                          </div>
                          <p className="text-xs text-stone-500">{b.start_date} to {b.end_date} &middot; ${b.total_price}</p>
                        </div>
                        <div className="flex gap-2 shrink-0">
                          <Link to={`/items/${b.item_id}`}>
                            <Button size="sm" className="rounded-full text-xs bg-coral hover:bg-[#d44814] text-white" data-testid={`retry-booking-${b.id}`}>
                              Try Again
                            </Button>
                          </Link>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </>
        );
      })()}
    </div>
  );
}

export default function Dashboard() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [role, setRole] = useState(null); // Always start with no role - show selector
  const [myItems, setMyItems] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reviewModal, setReviewModal] = useState(null);
  const [reviewData, setReviewData] = useState({ item_rating: 0, user_rating: 0, comment: "" });
  const [unreadMessages, setUnreadMessages] = useState(0);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate("/auth"); return; }
    Promise.all([
      api.get("/items", { params: { limit: 50 } }),
      api.get("/bookings"),
      api.get("/messages/unread-count"),
    ]).then(([itemsRes, bookingsRes, unreadRes]) => {
      setMyItems(itemsRes.data.items.filter((i) => i.owner_id === user.id));
      setBookings(bookingsRes.data);
      setUnreadMessages(unreadRes.data.unread_count || 0);
    }).catch(console.error).finally(() => setLoading(false));
  }, [user, authLoading, navigate]);

  const selectRole = (r) => {
    setRole(r);
  };

  const switchRole = () => {
    const newRole = role === "lender" ? "borrower" : "lender";
    selectRole(newRole);
  };

  const deleteItem = async (id) => {
    try {
      await api.delete(`/items/${id}`);
      setMyItems((prev) => prev.filter((i) => i.id !== id));
      toast.success("Item deleted");
    } catch { toast.error("Failed to delete"); }
  };

  const updateStatus = async (bookingId, status) => {
    try {
      await api.put(`/bookings/${bookingId}/status`, { status });
      setBookings((prev) => prev.map((b) => (b.id === bookingId ? { ...b, status } : b)));
      toast.success(`Booking ${status}`);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to update"); }
  };

  const cancelBooking = async (bookingId) => {
    if (!window.confirm("Are you sure you want to cancel this booking? Refund eligibility depends on the cancellation policy (48h+ before start = full refund).")) return;
    try {
      const res = await api.post(`/bookings/${bookingId}/cancel`);
      setBookings((prev) => prev.map((b) => (b.id === bookingId ? { ...b, status: 'cancelled', refund_status: res.data.refund_status } : b)));
      if (res.data.refund_processed) {
        toast.success("Booking cancelled. A full refund has been issued.");
      } else if (res.data.refund_eligible === false) {
        toast.info("Booking cancelled. This cancellation is within 48 hours of the start date and is non-refundable.");
      } else {
        toast.success("Booking cancelled.");
      }
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to cancel booking"); }
  };

  const submitReview = async () => {
    if (reviewData.item_rating === 0 || reviewData.user_rating === 0) {
      toast.error("Please rate both the item and the lender");
      return;
    }
    try {
      await api.post("/reviews", { booking_id: reviewModal.id, ...reviewData });
      setBookings((prev) => prev.map((b) => (b.id === reviewModal.id ? { ...b, has_review: true } : b)));
      setReviewModal(null);
      setReviewData({ item_rating: 0, user_rating: 0, comment: "" });
      toast.success("Review submitted!");
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to submit review"); }
  };

  if (authLoading || !user) return null;

  // Show role selector if no role chosen
  if (!role) {
    return <RoleSelector onSelect={selectRole} />;
  }

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-stone-100 rounded w-1/3" />
          <div className="grid grid-cols-4 gap-4">{[...Array(4)].map((_, i) => <div key={i} className="h-20 bg-stone-100 rounded-2xl" />)}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="font-heading font-bold text-2xl sm:text-3xl text-stone-900">
              {role === "lender" ? "Lender Dashboard" : "Borrower Dashboard"}
            </h1>
            <Badge className={`rounded-full text-xs ${role === "lender" ? "bg-coral-light text-coral border-orange-200" : "bg-emerald-100 text-emerald-700 border-emerald-200"}`}>
              {role === "lender" ? "Lending" : "Borrowing"}
            </Badge>
          </div>
          <p className="text-stone-500 text-sm mt-1">Welcome back, {user.name}</p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" onClick={switchRole} className="rounded-full gap-1.5 border-stone-200 text-stone-600 hover:text-stone-900" data-testid="switch-role-btn">
            <ArrowLeftRight className="w-4 h-4" />
            Switch to {role === "lender" ? "Borrowing" : "Lending"}
          </Button>
          {role === "lender" && (
            <Link to="/create-listing">
              <Button className="rounded-full bg-coral hover:bg-coral text-white gap-1.5 btn-press" data-testid="dashboard-create-listing">
                <Plus className="w-4 h-4" /> New Listing
              </Button>
            </Link>
          )}
          {role === "borrower" && (
            <Link to="/browse">
              <Button className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white gap-1.5 btn-press" data-testid="dashboard-browse-items">
                <Search className="w-4 h-4" /> Browse Items
              </Button>
            </Link>
          )}
        </div>
      </div>

      {/* Unread Messages Notification */}
      {unreadMessages > 0 && (
        <Link to="/messages" className="block mb-6" data-testid="dashboard-message-notification">
          <div className="bg-coral-light border border-orange-200 rounded-xl px-5 py-3.5 flex items-center justify-between hover:shadow-sm transition-shadow">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-coral flex items-center justify-center">
                <Bell className="w-4 h-4 text-white" />
              </div>
              <div>
                <p className="text-sm font-medium text-stone-900 font-ui">
                  You have {unreadMessages} unread message{unreadMessages !== 1 ? "s" : ""}
                </p>
                <p className="text-xs text-stone-500 font-ui">Tap to view your conversations</p>
              </div>
            </div>
            <ChevronRight className="w-4 h-4 text-stone-400" />
          </div>
        </Link>
      )}

      {/* Role-specific dashboard */}
      {role === "lender" ? (
        <LenderDashboard user={user} myItems={myItems} bookings={bookings} deleteItem={deleteItem} updateStatus={updateStatus} />
      ) : (
        <BorrowerDashboard user={user} bookings={bookings} cancelBooking={cancelBooking} reviewModal={reviewModal} setReviewModal={setReviewModal} reviewData={reviewData} setReviewData={setReviewData} submitReview={submitReview} />
      )}

      {/* Review Modal */}
      <Dialog open={!!reviewModal} onOpenChange={() => setReviewModal(null)}>
        <DialogContent className="rounded-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading">Write a Review</DialogTitle>
            <DialogDescription>Rate the item and lender for your recent borrowing experience.</DialogDescription>
          </DialogHeader>
          {/* Rating Key */}
          <div className="flex items-center gap-3 text-xs text-stone-400 bg-stone-50 rounded-lg px-3 py-2 mt-1" data-testid="rating-key">
            <span>1 = Poor</span>
            <span className="text-stone-300">|</span>
            <span>2 = Fair</span>
            <span className="text-stone-300">|</span>
            <span>3 = Good</span>
            <span className="text-stone-300">|</span>
            <span>4 = Very Good</span>
            <span className="text-stone-300">|</span>
            <span>5 = Excellent</span>
          </div>
          <div className="space-y-4 mt-2">
            <div>
              <label className="text-sm font-medium mb-1 block">Item Rating</label>
              <div className="flex gap-1">
                {[1, 2, 3, 4, 5].map((s) => (
                  <button key={s} type="button" onClick={() => setReviewData({ ...reviewData, item_rating: s })} className="cursor-pointer" data-testid={`item-star-${s}`}>
                    <Star className={`w-6 h-6 ${s <= reviewData.item_rating ? "fill-orange-400 text-orange-400" : "text-stone-300"}`} />
                  </button>
                ))}
                <span className="text-xs text-stone-400 ml-2 self-center">
                  {reviewData.item_rating === 0 ? "Select a rating" : ["", "Poor", "Fair", "Good", "Very Good", "Excellent"][reviewData.item_rating]}
                </span>
              </div>
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Lender Rating</label>
              <div className="flex gap-1">
                {[1, 2, 3, 4, 5].map((s) => (
                  <button key={s} type="button" onClick={() => setReviewData({ ...reviewData, user_rating: s })} className="cursor-pointer" data-testid={`user-star-${s}`}>
                    <Star className={`w-6 h-6 ${s <= reviewData.user_rating ? "fill-orange-400 text-orange-400" : "text-stone-300"}`} />
                  </button>
                ))}
                <span className="text-xs text-stone-400 ml-2 self-center">
                  {reviewData.user_rating === 0 ? "Select a rating" : ["", "Poor", "Fair", "Good", "Very Good", "Excellent"][reviewData.user_rating]}
                </span>
              </div>
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Comment</label>
              <Textarea placeholder="Share your experience..." value={reviewData.comment}
                onChange={(e) => setReviewData({ ...reviewData, comment: e.target.value })}
                className="rounded-xl" data-testid="review-comment-input" />
            </div>
            <Button onClick={submitReview} disabled={reviewData.item_rating === 0 || reviewData.user_rating === 0} className="w-full rounded-full bg-coral hover:bg-coral text-white btn-press disabled:opacity-50" data-testid="submit-review-btn">
              Submit Review
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
